import React, { useEffect, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Button } from '@/design-system'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

const Root = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 100,
  background: 'rgba(255, 255, 255, 0.86)',
  backdropFilter: 'blur(18px) saturate(1.5)',
})

const Inner = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  height: 62,
  padding: '0 clamp(20px, 4vw, 48px)',
  maxWidth: 1240,
  margin: '0 auto',
  '@media (max-width: 960px)': {
    gridTemplateColumns: '1fr auto',
    height: 56,
  },
})

const Brand = styled(RouterLink)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 16,
  fontWeight: 760,
  letterSpacing: '-0.03em',
  color: 'var(--ds-ink)',
  textDecoration: 'none',
})

const Co = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.16em',
  color: 'var(--ds-ink-subtle)',
  fontWeight: 500,
  marginLeft: 8,
  paddingLeft: 12,
  borderLeft: '1px solid var(--ds-border)',
  '@media (max-width: 720px)': { display: 'none' },
})

const Links = styled(Box)({
  display: 'flex',
  gap: 2,
  justifyContent: 'center',
  '@media (max-width: 960px)': { display: 'none' },
})

const NavLink = styled('a')({
  position: 'relative',
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 570,
  color: 'var(--ds-ink-muted)',
  borderRadius: 'var(--ds-radius-pill)',
  transition: 'color var(--ds-duration-fast) var(--ds-ease)',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': { color: 'var(--ds-ink)' },
})

const SideR = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  justifyContent: 'flex-end',
  '@media (max-width: 960px)': { gap: 8 },
})

const SignIn = styled(RouterLink)({
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--ds-ink-muted)',
  textDecoration: 'none',
  '&:hover': { color: 'var(--ds-ink)' },
  '@media (max-width: 960px)': { display: 'none' },
})

const MenuBtn = styled(IconButton)({
  display: 'none',
  color: 'var(--ds-ink-muted)',
  padding: 6,
  '@media (max-width: 960px)': { display: 'inline-flex' },
})

const MobileSheet = styled(Box, {
  shouldForwardProp: (p) => p !== 'open',
})<{ open: boolean }>(({ open }) => ({
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  gap: 2,
  padding: '10px clamp(20px, 4vw, 48px) 18px',
  borderTop: '1px solid var(--ds-border)',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(18px) saturate(1.5)',
}))

const mobileLinkStyles = {
  padding: '11px 4px',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--ds-ink)',
  textDecoration: 'none',
  borderBottom: '1px solid var(--ds-border)',
  display: 'block',
}

const MobileNavLink = styled('a')(mobileLinkStyles)

const MobileRouteLink = styled(RouterLink)(mobileLinkStyles)

const MobileAuthRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  paddingTop: 12,
  marginTop: 4,
  '@media (min-width: 481px)': { display: 'none' },
})

const HeaderCta = styled(Box)({
  '@media (max-width: 480px)': { display: 'none' },
})

const Progress = styled(Box)({
  position: 'absolute',
  left: 0,
  bottom: 0,
  height: 1.5,
  width: '100%',
  background: 'var(--ds-border)',
})

const ProgressFill = styled('i')({
  display: 'block',
  height: '100%',
  width: '0%',
  background: 'var(--ds-gradient-spectrum)',
  transition: 'width 80ms linear',
})

const SECTIONS = [
  { href: '#fidelity', label: 'Fidelity' },
  { href: '#interactions', label: 'Interactions' },
  { href: '#key', label: 'The Key' },
  { href: '#room', label: 'The Room' },
  { href: '#discover', label: 'Discover' },
  { href: '/pricing', label: 'Pricing', route: true },
]

/** Fixed marketing nav with a scroll-progress hairline — Landing v2. */
export function LandingNav() {
  const navigate = useNavigate()
  const [progress, setProgress] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (!menuOpen) return
    const root = document.getElementById('root')
    const prevBody = document.body.style.overflow
    const prevRoot = root?.style.overflow ?? ''
    document.body.style.overflow = 'hidden'
    if (root) root.style.overflow = 'hidden'

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = prevBody
      if (root) root.style.overflow = prevRoot
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 960) setMenuOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    // The app shell scrolls `#root` (position: absolute; overflow: auto),
    // not `window`/`document.documentElement` — fall back to window scroll
    // if that ever changes.
    const scroller = document.getElementById('root') ?? document.documentElement
    const onScroll = () => {
      const max = scroller.scrollHeight - scroller.clientHeight
      setProgress(max > 0 ? (scroller.scrollTop / max) * 100 : 0)
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      scroller.removeEventListener('scroll', onScroll)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  const closeMenu = () => setMenuOpen(false)

  const onSectionClick = (s: (typeof SECTIONS)[number]) => {
    closeMenu()
    if (s.route) navigate(s.href)
  }

  return (
    <Root>
      <Inner>
        <Brand to='/' onClick={closeMenu}>
          <LogoMark />
          {APP_NAME} <Co>EST. 2026 · FULL-RES ALWAYS</Co>
        </Brand>
        <Links>
          {SECTIONS.map((s) =>
            s.route ? (
              <NavLink key={s.href} as={RouterLink} href={s.href} onClick={(e) => { e.preventDefault(); navigate(s.href) }}>
                {s.label}
              </NavLink>
            ) : (
              <NavLink key={s.href} href={s.href}>
                {s.label}
              </NavLink>
            )
          )}
        </Links>
        <SideR>
          <SignIn to='/auth?step=login'>Sign in</SignIn>
          <HeaderCta>
            <Button variant='primary' size='sm' onClick={() => navigate('/auth?step=signup')}>
              Start free →
            </Button>
          </HeaderCta>
          <MenuBtn
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <CloseIcon fontSize='small' /> : <MenuIcon fontSize='small' />}
          </MenuBtn>
        </SideR>
      </Inner>
      <MobileSheet open={menuOpen} role='dialog' aria-modal='true' aria-label='Site navigation'>
        {SECTIONS.map((s) =>
          s.route ? (
            <MobileRouteLink
              key={s.href}
              to={s.href}
              onClick={(e) => {
                e.preventDefault()
                onSectionClick(s)
              }}
            >
              {s.label}
            </MobileRouteLink>
          ) : (
            <MobileNavLink key={s.href} href={s.href} onClick={closeMenu}>
              {s.label}
            </MobileNavLink>
          )
        )}
        <MobileAuthRow>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => {
              closeMenu()
              navigate('/auth?step=login')
            }}
          >
            Sign in
          </Button>
          <Button
            variant='primary'
            size='sm'
            onClick={() => {
              closeMenu()
              navigate('/auth?step=signup')
            }}
          >
            Start free →
          </Button>
        </MobileAuthRow>
      </MobileSheet>
      <Progress>
        <ProgressFill style={{ width: `${progress}%` }} />
      </Progress>
    </Root>
  )
}
